import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  useModelIndex,
  useModelCreate,
  useModelUpdate,
  useHasRoleIn,
  useModelInvalidateIndex
} from '@rhino-project/core/hooks';
import { formatMoney, roomMediaClass, statusBadgeClass } from '../lib/format';
import type { RoomRow } from '../lib/harborlightTypes';

// Ported 1:1 from the source repository (client/src/pages/RoomsPage.jsx).
// Data is served by the Rhino `room` model API (snake_case fields).

const emptyForm = {
  number: '',
  room_type: 'STANDARD',
  floor: 1,
  capacity: 2,
  price_per_night: 100,
  status: 'AVAILABLE',
  description: '',
  amenities: 'Wi-Fi, TV'
};

const RoomsPage = () => {
  const { owner } = useParams({ strict: false }) as { owner: string };
  const isStaff = useHasRoleIn(['staff', 'admin']);

  const { resources, error: loadError } = useModelIndex('room', { limit: 100 });
  const rooms = resources?.results ?? [];

  const invalidate = useModelInvalidateIndex('room');
  const { mutateAsync: createRoom, isPending: saving } = useModelCreate('room');
  const { mutateAsync: patchRoom } = useModelUpdate('room');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState<Record<string, unknown>>(emptyForm);

  async function createRoomSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await createRoom({
        ...form,
        floor: Number(form.floor),
        capacity: Number(form.capacity),
        price_per_night: Number(form.price_per_night),
        amenities: String(form.amenities)
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean)
      } as never);
      setForm(emptyForm);
      setSuccess('Room added.');
      invalidate.invalidate();
    } catch {
      setError(
        'Could not add room. Check the number is unique and fields are valid.'
      );
    }
  }

  async function updateStatus(roomId: number | string, status: string) {
    setError('');
    try {
      await patchRoom({ id: roomId, status } as never);
      invalidate.invalidate();
    } catch {
      setError('Could not update the room status.');
    }
  }

  return (
    <div className="fade-up">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
        <div>
          <p className="text-uppercase small text-secondary mb-1">Inventory</p>
          <h1 className="display-title display-5 mb-0">Rooms</h1>
        </div>
      </div>

      {(error || loadError) && (
        <div className="alert alert-danger">
          {error || 'Could not load rooms.'}
        </div>
      )}
      {success && <div className="alert alert-success">{success}</div>}

      {isStaff && (
        <div className="surface-panel p-4 mb-4">
          <h2 className="h5 display-title mb-3">Add room</h2>
          <form className="row g-3" onSubmit={createRoomSubmit}>
            <div className="col-md-2">
              <label className="form-label">Number</label>
              <input
                className="form-control"
                value={String(form.number)}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={String(form.room_type)}
                onChange={(e) =>
                  setForm({ ...form, room_type: e.target.value })
                }
              >
                <option value="STANDARD">Standard</option>
                <option value="DELUXE">Deluxe</option>
                <option value="SUITE">Suite</option>
                <option value="FAMILY">Family</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Floor</label>
              <input
                type="number"
                className="form-control"
                value={String(form.floor)}
                onChange={(e) => setForm({ ...form, floor: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Capacity</label>
              <input
                type="number"
                className="form-control"
                value={String(form.capacity)}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Price / night</label>
              <input
                type="number"
                className="form-control"
                value={String(form.price_per_night)}
                onChange={(e) =>
                  setForm({ ...form, price_per_night: e.target.value })
                }
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={String(form.status)}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="AVAILABLE">Available</option>
                <option value="OCCUPIED">Occupied</option>
                <option value="MAINTENANCE">Maintenance</option>
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label">Description</label>
              <input
                className="form-control"
                value={String(form.description)}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Amenities (comma separated)</label>
              <input
                className="form-control"
                value={String(form.amenities)}
                onChange={(e) =>
                  setForm({ ...form, amenities: e.target.value })
                }
              />
            </div>
            <div className="col-12">
              <button type="submit" className="btn btn-sea" disabled={saving}>
                {saving ? 'Saving…' : 'Add room'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="row g-4">
        {rooms.map((room: RoomRow) => (
          <div className="col-md-6 col-xl-4" key={room.id}>
            <article className="room-tile">
              <div className={roomMediaClass(room.room_type)} />
              <div className="p-3 d-flex flex-column flex-grow-1">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div>
                    <h2 className="h5 display-title mb-1">
                      Room {room.number}
                    </h2>
                    <div className="text-secondary small">
                      {room.room_type} · Floor {room.floor} · Sleeps{' '}
                      {room.capacity}
                    </div>
                  </div>
                  <span className={statusBadgeClass(room.status)}>
                    {room.status}
                  </span>
                </div>
                <p className="small text-secondary flex-grow-1">
                  {room.description}
                </p>
                <div className="d-flex flex-wrap gap-1 mb-3">
                  {room.amenities?.slice(0, 4).map((item: string) => (
                    <span key={item} className="badge badge-soft rounded-pill">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <strong>{formatMoney(room.price_per_night)}/night</strong>
                  <div className="d-flex align-items-center gap-2">
                    {room.status === 'AVAILABLE' && (
                      <Link
                        className="btn btn-copper btn-sm"
                        to="/$owner/rooms/$id/book"
                        params={{ owner, id: String(room.id) }}
                      >
                        Book
                      </Link>
                    )}
                    {isStaff && (
                      <select
                        className="form-select form-select-sm w-auto"
                        value={room.status}
                        onChange={(e) => updateStatus(room.id, e.target.value)}
                      >
                        <option value="AVAILABLE">Available</option>
                        <option value="OCCUPIED">Occupied</option>
                        <option value="MAINTENANCE">Maintenance</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            </article>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoomsPage;
